import {ChangeDetectionStrategy, Component, ElementRef, viewChild} from '@angular/core';
import Modal from "bootstrap/js/dist/modal";
import {sendQuery} from "../../common/utils";
import {
  FacetValueResult,
  FindHarbourDuesVisits,
  GetVesselImageUrls,
  HarbourDuesVisitSummary,
  HarbourDuesVisitSummarySortingProperty,
  SortDirection,
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../app-context";
import lodash from "lodash";
import {DateFieldRange, MomentDateFieldRange} from "../../common/date/date-range/date-field-range";
import moment from "moment/moment";
import {FacetStats, QuickView} from "../common/facets/facet-filter/facet-filter.component";
import {SentenceCasePipe} from "../../common/sentence-case.pipe";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {DataQuery, FacetedOverview} from "../common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../common/overview/overview.component";
import {Handler} from "../../common/handlers/handler";
import {HandleQuery} from "../../common/handlers/handle-query";
import {Observable, of} from "rxjs";
import {now} from "moment";
import {HarbourDuesOverviewUtils} from "./harbour-dues-overview-item/harbour-dues-overview.utils";
import {NewHarbourDuesVisitSummary} from "./harbour-dues-overview-item/harbour-dues-overview-item.component";

const quickViews: QuickView[] = [{
  code: "ALL_VISITS",
  name: "All visits",
  icon: "fa-calendar-days",
  facets: {}
}, {
  code: "ATTENTION_REQUIRED",
  name: "Attention required",
  icon: "fa-circle-exclamation",
  facets: {},
  queryFn: (query: DataQuery<FindHarbourDuesVisits>): DataQuery<FindHarbourDuesVisits> => {
    return ({
      ...query,
      payload: {
        ...query.payload,
        attentionRequired: true
      }
    });
  }
}];

@Component({
    selector: 'app-harbour-dues-overview',
    templateUrl: './harbour-dues-overview.component.html',
    styleUrls: ['./harbour-dues-overview.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
@Handler()
export class HarbourDuesOverviewComponent extends FacetedOverview<NewHarbourDuesVisitSummary, HarbourDueOverviewFilters> {
  appContext = AppContext;

  sortingProperties: HarbourDuesVisitSummarySorting[] = [{
    property: HarbourDuesVisitSummarySortingProperty.CREATED,
    direction: SortDirection.DESC
  }, {
    property: HarbourDuesVisitSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.DESC
  }, {
    property: HarbourDuesVisitSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.ASC
  }];
  cachedVisits: { [key: string]: HarbourDuesVisitSummary[] } = {};
  vesselImageModal = viewChild<ElementRef>('vesselImageModal');
  private vesselImageModalInstance: Modal;
  zoomedVesselImageUrl: string;
  hiddenFacets: string[] = ["count", "ownerShortName", "declarantShortName", "allowedViewers", "actionRequiredForAgents",
    "actionRequiredForStevedores", "actionRequiredForShipOperator", "activeDeadlinesForStevedores", "hasActiveDeadline",
    "visitStatus", "portName", "stevedore", "quay", "tidalShip", "rtaUnequalEta", "unequalEtc", "unequalMooring",
    "unequalBollards", "overlandedContainers", "shortlandedContainers", "harbourMasterStatus", "HarbourDues/status",
    "unreadHarbourRemarks", "bunkeringLate", "hasHarbourDues", "willBeTransferred", "exchange",
    "actionRequiredForFinancialDeclarant", "etaFirstBerth"]
    .map(h => h.toLowerCase());
  totalCount: number = 0;

  constructor() {
    super("harbour-dues-overview-filters", quickViews, quickViews.find(q => q.code === "ATTENTION_REQUIRED"));
  }

  getDataQuery = (term: string): DataQuery<FindHarbourDuesVisits> => {
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange, !term, {
      start: "2023-01-01T00:00:00.000Z",
      end: moment(now()).endOf("date").add(1, "quarter").toISOString()
    });
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<FindHarbourDuesVisits>>{
      className: "com.portbase.bezoekschip.common.api.harbourdues.FindHarbourDuesVisits",
      payload: {
        term: term,
        dateTimeRange: timeRange,
        pagination: { from: this.from, count: this.maxItems},
        sortingProperty: this.sortingProperty.property,
        sortDirection: this.sortingProperty.direction,
        facetFilters: this.getFacetFilters()
      }
    };

    return queryFn ? queryFn(query) : query;
  }

  get sortingProperty(): HarbourDuesVisitSummarySorting {
    return this.filters?.sorting || (this.filters?.sortingProperty ? {
      property: this.filters.sortingProperty,
      direction: this.filters.sortDirection
    } : null) || {
      property: HarbourDuesVisitSummarySortingProperty.ARRIVAL_DATE,
      direction: SortDirection.DESC
    };
  }

  getHiddenFacets = (): string[] => [];

  changeSorting = (sortingProperty: HarbourDuesVisitSummarySorting) => {
    this.filters.sorting = sortingProperty;
    this.filters.sortDirection = this.filters.sortingProperty = null;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  arrivalDateChanged = (date: DateFieldRange) => {
    this.filters.overviewFilters.filters.timeRange = date ? <DateFieldRange>{
      start: moment(date.start).toISOString(),
      end: moment(date.end).toISOString()
    } : null;
    this.data = [];
    this.loadData();
  }

  private updateVesselImages(result: HarbourDuesVisitSummary[]) {
    return sendQuery("com.portbase.bezoekschip.common.api.visit.GetVesselImageUrls", <GetVesselImageUrls>{
      imoCodes: result.map(visit => visit.imoCode)
    }, {showSpinner: false, caching: false}).subscribe(vesselImages => {
        this.data = this.data.map(visit => {
          if (vesselImages.hasOwnProperty(visit.imoCode)) {
            return {
              ...visit,
              vesselImageUrl: vesselImages[visit.imoCode]
            }
          }
          return visit;
        });
      }
    );
  }

  renderRecords = (result: HarbourDuesVisitSummary[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (!append) {
      this.cachedVisits = {};
    }
    const visits = result.map(v => HarbourDuesOverviewUtils.toExtendedSummary(v));
    Object.entries(lodash.groupBy(visits, (v) => v.visitStatus))
      .forEach(e => this.cachedVisits[e[0]] = (this.cachedVisits[e[0]] || []).concat(e[1]))
    if (visits.length < this.maxItems) {
      this.endReached = true;
    }
    this.data = append ? (this.data || []).concat(visits) : visits;
    this.updateVesselImages(result)
  }

  protected setFacetsFromResult(facets: { [p: string]: FacetValueResult[] }) {
    super.setFacetsFromResult(facets);
    const countFacet = this.allFacets?.find(e => e?.name.toLowerCase() === "count");
    this.totalCount = countFacet ? lodash.sum(countFacet.values.map(e => e.count)) : 0;
  }

  trackByRecord = (index: number, obj: NewHarbourDuesVisitSummary): any => obj.crn;

  openVesselImageModal = (summary: NewHarbourDuesVisitSummary) => {
    this.zoomedVesselImageUrl = summary.vesselImageUrl;
    const vesselImageModal = this.vesselImageModal();
    if (!vesselImageModal) {
      return;
    }
    this.vesselImageModalInstance = new Modal(vesselImageModal.nativeElement);
    this.vesselImageModalInstance.show();
  }

  nameFormatter = (name: string): string => {
    switch (name) {
      case "harbourDues/status": return "Harbour dues status";
      default: return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  private getSortDirection(property: HarbourDuesVisitSummarySorting): SortDirection {
    return property.direction;
  }

  getSortDirectionFormatter(sortDirection: SortDirection): string {
    switch (sortDirection) {
      case SortDirection.ASC: return "ascending";
      case SortDirection.DESC: return "descending";
    }
  }

  static visitStatusFormatter: (value: string) => string = v => {
    if (v === "ARRIVED") return "In Port"
    return PortvisitUtils.enumFormatter(v);
  };

  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "visitStatus": return HarbourDuesOverviewComponent.visitStatusFormatter;
      case "orderStatus": case "harbourDues/status": return PortvisitUtils.enumFormatter;
      default: return null;
    }
  }

  getValuesSelectedCallback = (name: string): (facet: FacetStats, selectedValues: string[]) => string[] => {
    switch (name) {
      case "overlandedContainers": case "shortlandedContainers":
        return (facet, selectedValues) => selectedValues.includes("true")
          ? AppContext.isAdmin() ? ["ADMIN"] : [AppContext.userProfile.organisation.shortName]
          : [];
      default: return null;
    }
  }

  protected sortingFormatter = (sortingProperty: HarbourDuesVisitSummarySorting): string => {
    switch (sortingProperty.property) {
      case HarbourDuesVisitSummarySortingProperty.ARRIVAL_DATE: return `Arrival (${this.getSortDirectionFormatter(sortingProperty.direction)})`;
      case HarbourDuesVisitSummarySortingProperty.CREATED: return `Created (${this.getSortDirectionFormatter(sortingProperty.direction)})`;
    }
  }

  filterFacetValues = (name: string, values: FacetValueResult[]): FacetValueResult[] => {
    return values;
  }

  ranges: MomentDateFieldRange[] = [
    {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().add(1, "day").startOf('day').subtract(1, 'day')
    },
    {
      label: 'Yesterday',
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().startOf('day').subtract(1, 'day')
    },
    {
      label: 'This week',
      start: moment().startOf('week'),
      end: moment().add(1, "week").startOf('week').subtract(1, 'day')
    },
    {
      label: 'Last 7 days',
      start: moment().subtract(7, 'day').startOf('day'),
      end: moment().add(1, "day").startOf('day')
    },
    {
      label: 'Next 7 days',
      start: moment().startOf('day'),
      end: moment().add(7, "day").startOf('day').subtract(1, 'day')
    }
  ];

  @HandleQuery("getFacetValues", { global: true })
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }
}

interface HarbourDueOverviewFilters extends FacetedOverviewFilters<HarbourDueOverviewFacetFilters> {
  sortingProperty?: HarbourDuesVisitSummarySortingProperty;
  sortDirection?: SortDirection;
  sorting?: HarbourDuesVisitSummarySorting;
}

interface HarbourDueOverviewFacetFilters {
  timeRange?: DateFieldRange;
}

interface HarbourDuesVisitSummarySorting {
  property: HarbourDuesVisitSummarySortingProperty;
  direction: SortDirection;
}
